<template>
  <Disclosure v-slot="{ open }" as="nav" class="bg-white shadow">
    <AtContainer short>
      <div class="flex justify-between h-16">
        <div class="flex">
          <div class="flex-shrink-0 flex items-center">
            <router-link :to="{ name: 'App.Index' }" data-testid="base_logo_link">
              <BaseLogo class="block h-8 w-auto text-primary-600" />
            </router-link>
          </div>
          <div class="hidden md:ml-6 md:flex md:space-x-8">
            <!-- Current: "border-primary-600 text-gray-900", Default: "border-white text-gray-500 hover:border-gray-300 hover:text-gray-700" -->
            <router-link
              v-for="(link, index) in links"
              :key="'navbar-link-' + index"
              :to="link.to"
              class="border-white text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2"
              exact-active-class="border-primary-600 text-gray-900"
            >
              {{ link.title }}
            </router-link>
          </div>
        </div>
        <div class="hidden md:ml-6 md:flex md:items-center">
          <slot name="action" />

          <!-- Profile dropdown -->
          <Menu as="div" class="ml-3 relative">
            <div>
              <MenuButton
                class="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <template v-if="user.avatar_link">
                  <span class="sr-only">Open user menu</span>
                  <img class="h-8 w-8 rounded-full" :src="user.avatar_link" alt="avatar" />
                </template>
                <template v-else>
                  <span class="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500">
                    <span class="text-sm font-medium leading-none text-white">
                      {{ user.shortName }}
                    </span>
                  </span>
                </template>
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="z-10 origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                <MenuItem>
                  <router-link :to="{ name: 'Profile' }" class="block px-4 py-2 text-sm text-gray-700">
                    {{ $t('system.btns.profile.settings') }}
                  </router-link>
                </MenuItem>
                <MenuItem>
                  <a href="#" class="block px-4 py-2 text-sm text-gray-700" @click="$emit('logout')">
                    {{ $t('system.btns.profile.exit') }}
                  </a>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
        <div class="-mr-2 flex items-center md:hidden">
          <!-- Mobile menu button -->
          <DisclosureButton
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
          >
            <span class="sr-only">Open main menu</span>
            <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
            <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </AtContainer>
    <DisclosurePanel class="md:hidden">
      <div class="pt-2 pb-3 space-y-1">
        <!-- Current: "bg-primary-100 border-primary-600 text-primary-700", Default: "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700" -->
        <DisclosureButton v-for="(link, index) in links" :key="'navbar-link-' + index" as="template">
          <router-link
            :to="link.to"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            exact-active-class="bg-primary-100 border-primary-600 text-primary-700"
          >
            {{ link.title }}
          </router-link>
        </DisclosureButton>
      </div>
      <div class="px-4 pt-4 pb-3 border-t border-gray-200">
        <slot name="action" />
      </div>
      <div class="pt-4 pb-3 border-t border-gray-200">
        <div class="flex items-center px-4">
          <div class="flex-shrink-0">
            <template v-if="user.avatar_link">
              <span class="sr-only">Open user menu</span>
              <img class="h-10 w-10 rounded-full" :src="user.avatar_link" alt="avatar" />
            </template>
            <template v-else>
              <span class="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500">
                <span class="text-sm font-medium leading-none text-white">{{ user.shortName }}</span>
              </span>
            </template>
          </div>
          <div class="ml-3">
            <div class="text-base font-medium text-gray-800">{{ user.name }} {{ user.surname }}</div>
            <div class="text-sm font-medium text-gray-500">
              {{ formatPhoneNational(user.phone) }}
            </div>
          </div>
        </div>
        <div class="mt-3 space-y-1">
          <DisclosureButton class="text-left w-full">
            <router-link
              :to="{ name: 'Profile' }"
              class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            >
              {{ $t('system.btns.profile.settings') }}
            </router-link>
          </DisclosureButton>
          <DisclosureButton
            as="a"
            href="#"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            @click="$emit('logout')"
          >
            {{ $t('system.btns.profile.exit') }}
          </DisclosureButton>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<script setup>
  import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
  } from '@headlessui/vue';
  import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline';

  import BaseLogo from '~/components/base/Logo';

  import { formatPhoneNational } from '~/utils/formatPhoneNumber';

  import AtContainer from '../ui/AtContainer.vue';

  defineProps({
    links: {
      type: Array,
      default: () => [],
      // validator: (links) =>
      //   links.every(
      //     (link) => hasOwnProperty(link, "title") && hasOwnProperty(link, "to")
      //   ),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  });
  defineEmits(['logout']);
</script>
